import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
  ReactNode,
} from "react";
import cn from "clsx";
import { useT } from "../i18n";

interface ConfirmOpts {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface ModalCtx {
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
}

const Ctx = createContext<ModalCtx | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [opts, setOpts] = useState<ConfirmOpts | null>(null);
  const [resolver, setResolver] = useState<((v: boolean) => void) | null>(null);

  const confirm = useCallback((o: ConfirmOpts) => {
    return new Promise<boolean>((resolve) => {
      setOpts(o);
      setResolver(() => resolve);
    });
  }, []);

  const close = (v: boolean) => {
    resolver?.(v);
    setOpts(null);
    setResolver(null);
  };

  return (
    <Ctx.Provider value={{ confirm }}>
      {children}
      <Dialog.Root open={!!opts} onOpenChange={(o) => !o && close(false)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in z-40" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-surface border border-border rounded-2xl shadow-2xl p-5 w-[420px] max-w-[90vw] animate-modal-in">
            <Dialog.Title className="font-semibold text-base text-text">
              {opts?.title}
            </Dialog.Title>
            {opts?.description && (
              <Dialog.Description className="text-sm text-text-2 mt-2">
                {opts.description}
              </Dialog.Description>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => close(false)}
                className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-surface-3 text-text"
              >
                {opts?.cancelLabel ?? t.common.abbrechen}
              </button>
              <button
                onClick={() => close(true)}
                className={
                  opts?.destructive
                    ? "px-3 py-1.5 text-sm rounded-md bg-danger hover:bg-danger/90 text-white"
                    : "px-3 py-1.5 text-sm rounded-md bg-accent-500 hover:bg-accent-600 text-white"
                }
              >
                {opts?.confirmLabel ?? t.common.bestaetigen}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Ctx.Provider>
  );
}

export function useModal(): ModalCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useModal outside provider");
  return v;
}

// ---------------------------------------------------------------------------
// <Modal> — einheitlicher Radix-Dialog-Wrapper für alle handgerollten Modals.
// Zentriert, bg-surface, rounded-2xl, shadow-2xl, Backdrop bg-black/50, ESC +
// Focus-Trap (via Radix). Verhalten identisch zu den bisherigen Hüllen.
// ---------------------------------------------------------------------------

export type ModalSize = "sm" | "md" | "lg" | "full";

/** Bildschirm-Punkt (Client-Koordinaten, z. B. aus einem Zeiger-Ereignis). */
export interface Point {
  x: number;
  y: number;
}

/** Mindestabstand zum Fensterrand, wenn ein Modal am Zeiger sitzt. */
const ANCHOR_MARGIN = 8;

/**
 * Modal-MITTE auf `pt` legen und dabei komplett im Viewport halten: passt die
 * Box nicht mehr, wird sie hineingeschoben (nie abgeschnitten). Ist sie in einer
 * Achse groesser als das Fenster, bleibt nur Zentrieren — dort greift im Modal
 * ohnehin `max-h-[90vh]` / `max-w`. Rein rechnerisch (keine DOM-Abhängigkeit)
 * und deshalb separat per Dry-Run abgesichert: frontend/modal-anchor-dryrun.mjs
 * (7 Fälle, Muster calendar-grid-dryrun.mts).
 */
export function clampAnchor(
  pt: Point,
  size: { w: number; h: number },
  vp: { w: number; h: number },
  margin = ANCHOR_MARGIN
): Point {
  const halfW = size.w / 2;
  const halfH = size.h / 2;
  const x =
    size.w + margin * 2 >= vp.w
      ? vp.w / 2
      : Math.min(Math.max(pt.x, halfW + margin), vp.w - halfW - margin);
  const y =
    size.h + margin * 2 >= vp.h
      ? vp.h / 2
      : Math.min(Math.max(pt.y, halfH + margin), vp.h - halfH - margin);
  return { x, y };
}

const MODAL_SIZE: Record<ModalSize, string> = {
  sm: "w-[420px] max-w-[92vw]",
  md: "w-[560px] max-w-[92vw]",
  lg: "w-[720px] max-w-[96vw]",
  full: "w-[min(1100px,96vw)]",
};

export interface ModalProps {
  open?: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  size?: ModalSize;
  /** Footer-Slot (Aktionen), sitzt unten mit Trennlinie. */
  footer?: ReactNode;
  /** Icon links neben dem Titel. */
  icon?: ReactNode;
  /** Zusätzlicher Slot rechts neben dem Titel (vor dem Schließen-Button),
      z. B. ein Segmented-Control zum Umschalten der Ansicht. Ändert diesen
      Slot NICHT die Modal-Instanz → kein Remount / keine animate-modal-in. */
  headerExtra?: ReactNode;
  /** Zusätzliche Klassen für den Content-Container. */
  className?: string;
  /** Body ohne Standard-Padding rendern (z. B. für Editor-Splits). */
  bodyClassName?: string;
  /**
   * Optional: Modal-MITTE auf diesen Bildschirm-Punkt legen (Client-Koordinaten
   * eines Zeiger-Ereignisses) statt in die Viewport-Mitte. Für Quick-Create-
   * Dialoge, die dort aufgehen sollen, wo geklickt wurde (Outlook-Verhalten).
   * Wird immer so weit hineingeschoben, dass nichts abgeschnitten ist.
   * NICHT gesetzt / undefined → unverändert zentriert (Default aller Modals).
   */
  anchorPoint?: Point | null;
  /**
   * Radix-Ausstiege für den Fokus. NICHT gesetzt → unverändertes Verhalten
   * (Radix nimmt beim Öffnen das erste fokussierbare Element — das ist der
   * Schließen-Knopf im Kopf — und gibt den Fokus beim Schließen an das
   * Element zurück, das ihn vorher hatte).
   *
   * Der Verschieben-Dialog braucht beides anders: der Fokus soll im Filterfeld
   * landen (der Dialog ist ein Sprung ins Tippen), und zurückgehen soll er an
   * das auslösende Bedienelement — das ist ein `span[tabindex=-1]`, den Radix
   * nicht zuverlässig wiederfindet. `event.preventDefault()` im Handler
   * übernimmt die Steuerung.
   */
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
  children: ReactNode;
}

export function Modal({
  open = true,
  onClose,
  title,
  description,
  size = "md",
  footer,
  icon,
  headerExtra,
  className,
  bodyClassName,
  anchorPoint,
  onOpenAutoFocus,
  onCloseAutoFocus,
  children,
}: ModalProps) {
  // Zeiger-Positionierung: die Zentrierung selbst macht translate(-50%,-50%)
  // (braucht keine Maße, sitzt also schon im ERSTEN Frame auf dem Cursor); für
  // das Hineinschieben werden Box + Fenster gemessen. useLayoutEffect läuft vor
  // dem Paint → kein sichtbarer Sprung. ResizeObserver, weil das Modal wächst
  // (Teilnehmer-Picker aufklappen, Fehlermeldung) und dann neu geklemmt werden
  // muss; window.resize, weil ein kleineres Fenster neu klemmen muss.
  //
  // Callback-Ref statt useRef: Radix mountet den Content über <Presence> ERST
  // nach dem ersten Commit — ein useRef wäre im Layout-Effect noch null und das
  // Hineinschieben würde still ausfallen (belegt: Klick am oberen Rand landete
  // ungeklemmt bei top = −214 px). Der State-Wechsel beim Anhängen triggert die
  // Messung zuverlässig.
  const t = useT();
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const [box, setBox] = useState<{
    size: { w: number; h: number };
    vp: { w: number; h: number };
  } | null>(null);
  const ax = anchorPoint?.x;
  const ay = anchorPoint?.y;
  const anchored = ax !== undefined && ay !== undefined;

  useLayoutEffect(() => {
    if (!anchored || !open || !contentEl) return;
    const measure = () =>
      setBox({
        size: { w: contentEl.offsetWidth, h: contentEl.offsetHeight },
        vp: { w: window.innerWidth, h: window.innerHeight },
      });
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(contentEl);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [anchored, open, contentEl, ax, ay]);

  // Vor der ersten Messung: roher Cursor-Punkt (bereits mittig, nur noch
  // ungeklemmt) — danach die geklemmte Position.
  const pos =
    anchored && ax !== undefined && ay !== undefined
      ? box
        ? clampAnchor({ x: ax, y: ay }, box.size, box.vp)
        : { x: ax, y: ay }
      : null;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content
          // Nur der Zeiger-Fall misst; ohne `anchorPoint` hängt kein Ref, läuft
          // kein Effect und kein State-Update → Pfad identisch zu vorher.
          ref={anchored ? setContentEl : undefined}
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
          data-modal-anchored={pos ? "" : undefined}
          style={
            pos
              ? { left: pos.x, top: pos.y, transform: "translate(-50%, -50%)" }
              : undefined
          }
          className={cn(
            "fixed z-50",
            // Zentriert = Default; am Zeiger kommen left/top/transform aus style.
            !pos && "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            "bg-surface border border-border rounded-2xl shadow-2xl",
            "max-h-[90vh] flex flex-col animate-modal-in overscroll-contain focus:outline-none",
            MODAL_SIZE[size],
            className
          )}
        >
          {(title || icon || headerExtra) && (
            <div className="px-5 py-3 border-b border-border flex items-center gap-2 shrink-0">
              {icon && <span className="shrink-0 text-accent-500">{icon}</span>}
              <div className="min-w-0 flex-1">
                <Dialog.Title className="font-semibold text-sm text-text truncate">
                  {title}
                </Dialog.Title>
                {description && (
                  <Dialog.Description className="text-xs text-text-2 mt-0.5">
                    {description}
                  </Dialog.Description>
                )}
              </div>
              {headerExtra && <div className="shrink-0">{headerExtra}</div>}
              <Dialog.Close asChild>
                <button
                  className="p-1 rounded-md text-text-3 hover:text-text hover:bg-surface-3"
                  aria-label={t.common.schliessen}
                >
                  <X className="w-4 h-4" />
                </button>
              </Dialog.Close>
            </div>
          )}
          <div className={cn("flex-1 overflow-y-auto overscroll-contain", bodyClassName ?? "px-5 py-4")}>
            {children}
          </div>
          {footer && (
            <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 shrink-0">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default Modal;
